import { TargetingKeyMissingError, OpenFeatureEventEmitter, ProviderStatus, FlagNotFoundError, TypeMismatchError, StandardResolutionReasons, ProviderEvents, OpenFeature } from '@openfeature/web-sdk';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

/**
 * transformContext takes the raw OpenFeature context returns a GoFeatureFlagEvaluationContext.
 * @param context - the context used for flag evaluation.
 * @returns {GoFeatureFlagEvaluationContext} the user against who we will evaluate the flag.
 */
function transformContext(context) {
    const { targetingKey } = context, attributes = __rest(context, ["targetingKey"]);
    if (targetingKey === undefined || targetingKey === null || targetingKey === '') {
        throw new TargetingKeyMissingError();
    }
    return {
        key: targetingKey,
        custom: attributes,
    };
}

/**
 * FetchError is a wrapper around the HTTP error returned by
 * the method fetch.
 * It allows to throw an error with the status code.
 */
class FetchError extends Error {
    constructor(status) {
        super(`Request failed with status code ${status}`);
        this.status = status;
    }
}

class GoFeatureFlagWebProvider {
    constructor(options, logger) {
        this._websocketPath = 'ws/v1/flag/change';
        this.metadata = {
            name: GoFeatureFlagWebProvider.name,
        };
        this.events = new OpenFeatureEventEmitter();
        // status of the provider
        this._status = ProviderStatus.NOT_READY;
        // _flags is the in memory representation of all the flags.
        this._flags = {};
        this._logger = logger;
        this._apiTimeout = options.apiTimeout || 0; // default is 0 = no timeout
        this._endpoint = options.endpoint;
        this._retryInitialDelay = options.retryInitialDelay || 100;
        this._retryDelayMultiplier = options.retryDelayMultiplier || 2;
        this._maxRetries = options.maxRetries || 10;
        this._apiKey = options.apiKey;
    }
    get status() {
        return this._status;
    }
    initialize(context) {
        return __awaiter(this, void 0, void 0, function* () {
            return Promise.all([this.fetchAll(context), this.connectWebsocket()])
                .then(() => {
                var _a;
                this._status = ProviderStatus.READY;
                (_a = this._logger) === null || _a === void 0 ? void 0 : _a.debug(`${GoFeatureFlagWebProvider.name}: go-feature-flag provider initialized`);
            })
                .catch((error) => {
                var _a;
                (_a = this._logger) === null || _a === void 0 ? void 0 : _a.error(`${GoFeatureFlagWebProvider.name}: initialization failed, provider is on error, we will try to reconnect: ${error}`);
                this._status = ProviderStatus.ERROR;
                this.handleFetchErrors(error);
                // The initialization of the provider is in a failing state, we unblock the initialize method,
                // and we launch the retry to fetch the data.
                this.retryFetchAll(context);
                this.reconnectWebsocket();
            });
        });
    }
    /**
     * connectWebsocket is starting the websocket and associate some handler
     * to react if the state of the websocket change.
     */
    connectWebsocket() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            const wsURL = new URL(this._endpoint);
            wsURL.pathname = wsURL.pathname.endsWith('/')
                ? wsURL.pathname + this._websocketPath
                : wsURL.pathname + '/' + this._websocketPath;
            wsURL.protocol = wsURL.protocol === 'https:' ? 'wss' : 'ws';
            // adding API Key if GO Feature Flag use api keys.
            if (this._apiKey) {
                wsURL.searchParams.set('apiKey', this._apiKey);
            }
            (_a = this._logger) === null || _a === void 0 ? void 0 : _a.debug(`${GoFeatureFlagWebProvider.name}: Trying to connect the websocket at ${wsURL}`);
            this._websocket = new WebSocket(wsURL);
            yield this.waitWebsocketFinalStatus(this._websocket);
            this._websocket.onopen = (event) => {
                var _a;
                (_a = this._logger) === null || _a === void 0 ? void 0 : _a.info(`${GoFeatureFlagWebProvider.name}: Websocket to go-feature-flag open: ${event}`);
            };
            this._websocket.onmessage = (_a) => __awaiter(this, [_a], void 0, function* ({ data }) {
                var _b;
                (_b = this._logger) === null || _b === void 0 ? void 0 : _b.info(`${GoFeatureFlagWebProvider.name}: Change in your configuration flag`);
                const t = JSON.parse(data);
                const flagsChanged = this.extractFlagNamesFromWebsocket(t);
                yield this.retryFetchAll(OpenFeature.getContext(), flagsChanged);
            });
            this._websocket.onclose = () => __awaiter(this, void 0, void 0, function* () {
                var _a;
                (_a = this._logger) === null || _a === void 0 ? void 0 : _a.warn(`${GoFeatureFlagWebProvider.name}: Websocket closed, trying to reconnect`);
                yield this.reconnectWebsocket();
            });
            this._websocket.onerror = (event) => __awaiter(this, void 0, void 0, function* () {
                var _a;
                (_a = this._logger) === null || _a === void 0 ? void 0 : _a.error(`${GoFeatureFlagWebProvider.name}: Error while connecting the websocket: ${event}`);
                yield this.reconnectWebsocket();
            });
        });
    }
    /**
     * waitWebsocketFinalStatus is waiting synchronously for the websocket to be in a stable
     * state (CLOSED or OPEN).
     * @param socket - the websocket you are waiting for
     */
    waitWebsocketFinalStatus(socket) {
        return new Promise((resolve) => {
            const checkConnection = () => {
                if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CLOSED) {
                    return resolve();
                }
                // Wait 5 milliseconds before checking again
                setTimeout(checkConnection, 5);
            };
            checkConnection();
        });
    }
    /**
     * extract flag names from the websocket answer
     */
    extractFlagNamesFromWebsocket(wsResp) {
        let flags = [];
        if (wsResp.deleted) {
            flags = [...flags, ...Object.keys(wsResp.deleted)];
        }
        if (wsResp.updated) {
            flags = [...flags, ...Object.keys(wsResp.updated)];
        }
        if (wsResp.added) {
            flags = [...flags, ...Object.keys(wsResp.added)];
        }
        return flags;
    }
    /**
     * reconnectWebsocket is using an exponential backoff pattern to try to restart the connection
     * to the websocket.
     */
    reconnectWebsocket() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            let delay = this._retryInitialDelay;
            let attempt = 0;
            while (attempt < this._maxRetries) {
                attempt++;
                yield this.connectWebsocket();
                if (this._websocket !== undefined && this._websocket.readyState === WebSocket.OPEN) {
                    return;
                }
                yield new Promise((resolve) => setTimeout(resolve, delay));
                delay *= this._retryDelayMultiplier;
                (_a = this._logger) === null || _a === void 0 ? void 0 : _a.info(`${GoFeatureFlagWebProvider.name}: error while reconnecting the websocket, next try in ${delay} ms (${attempt}/${this._maxRetries}).`);
            }
            this.events.emit(ProviderEvents.Stale, {
                message: 'impossible to get status from GO Feature Flag (websocket connection stopped)',
            });
        });
    }
    onClose() {
        var _a;
        (_a = this._websocket) === null || _a === void 0 ? void 0 : _a.close(1000, 'Closing GO Feature Flag provider');
        return Promise.resolve();
    }
    onContextChange(_, newContext) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            (_a = this._logger) === null || _a === void 0 ? void 0 : _a.debug(`${GoFeatureFlagWebProvider.name}: new context provided: ${newContext}`);
            this.events.emit(ProviderEvents.Stale, { message: 'context has changed' });
            yield this.retryFetchAll(newContext);
            this.events.emit(ProviderEvents.Ready, { message: '' });
        });
    }
    resolveNumberEvaluation(flagKey) {
        return this.evaluate(flagKey, 'number');
    }
    resolveObjectEvaluation(flagKey) {
        return this.evaluate(flagKey, 'object');
    }
    resolveStringEvaluation(flagKey) {
        return this.evaluate(flagKey, 'string');
    }
    resolveBooleanEvaluation(flagKey) {
        return this.evaluate(flagKey, 'boolean');
    }
    evaluate(flagKey, type) {
        var _a;
        const resolved = this._flags[flagKey];
        if (!resolved) {
            throw new FlagNotFoundError(`flag key ${flagKey} not found in cache`);
        }
        if (typeof resolved.value !== type) {
            throw new TypeMismatchError(`flag key ${flagKey} is not of type ${type}`);
        }
        return {
            variant: resolved.variant,
            value: resolved.value,
            flagMetadata: resolved.flagMetadata,
            errorCode: resolved.errorCode,
            errorMessage: resolved.errorMessage,
            reason: ((_a = this._websocket) === null || _a === void 0 ? void 0 : _a.readyState) !== WebSocket.OPEN ? StandardResolutionReasons.CACHED : resolved.reason,
        };
    }
    retryFetchAll(ctx_1) {
        return __awaiter(this, arguments, void 0, function* (ctx, flagsChanged = []) {
            var _a;
            let delay = this._retryInitialDelay;
            let attempt = 0;
            while (attempt < this._maxRetries) {
                attempt++;
                try {
                    yield this.fetchAll(ctx, flagsChanged);
                    this._status = ProviderStatus.READY;
                    return;
                }
                catch (err) {
                    this._status = ProviderStatus.ERROR;
                    this.handleFetchErrors(err);
                    yield new Promise((resolve) => setTimeout(resolve, delay));
                    delay *= this._retryDelayMultiplier;
                    (_a = this._logger) === null || _a === void 0 ? void 0 : _a.info(`${GoFeatureFlagWebProvider.name}: Waiting ${delay} ms before trying to evaluate the flags (${attempt}/${this._maxRetries}).`);
                }
            }
        });
    }
    /**
     * fetchAll is a function that is calling GO Feature Flag to bulk evaluate flags.
     * It emits an event to notify when it is ready or on error.
     *
     * @param context - The static evaluation context
     * @param flagsChanged - The list of flags update - default: []
     * @private
     */
    fetchAll(context_1) {
        return __awaiter(this, arguments, void 0, function* (context, flagsChanged = []) {
            const endpointURL = new URL(this._endpoint);
            const path = 'v1/allflags';
            endpointURL.pathname = endpointURL.pathname.endsWith('/')
                ? endpointURL.pathname + path
                : endpointURL.pathname + '/' + path;
            const request = { evaluationContext: transformContext(context) };
            const init = {
                method: 'POST',
                headers: Object.assign({ 'Content-Type': 'application/json', Accept: 'application/json' }, (this._apiKey ? { Authorization: `Bearer ${this._apiKey}` } : {})),
                body: JSON.stringify(request),
            };
            const response = yield fetch(endpointURL.toString(), init);
            if (!(response === null || response === void 0 ? void 0 : response.ok)) {
                throw new FetchError(response.status);
            }
            const data = (yield response.json());
            // In case we are in success
            let flags = {};
            Object.keys(data.flags).forEach((currentValue) => {
                const resolved = data.flags[currentValue];
                const resolutionDetails = {
                    value: resolved.value,
                    variant: resolved.variationType,
                    errorCode: resolved.errorCode,
                    flagMetadata: resolved.metadata,
                    reason: resolved.reason,
                };
                flags = Object.assign(Object.assign({}, flags), { [currentValue]: resolutionDetails });
            });
            const hasFlagsLoaded = this._flags !== undefined && Object.keys(this._flags).length !== 0;
            this._flags = flags;
            if (hasFlagsLoaded) {
                this.events.emit(ProviderEvents.ConfigurationChanged, {
                    message: 'flag configuration have changed',
                    flagsChanged: flagsChanged,
                });
            }
        });
    }
    /**
     * handleFetchErrors is a function that take care of the errors that can be thrown
     * inside the FetchAll method.
     *
     * @param error - The error thrown
     * @private
     */
    handleFetchErrors(error) {
        var _a, _b, _c, _d;
        if (error instanceof FetchError) {
            this.events.emit(ProviderEvents.Error, {
                message: error.message,
            });
            if (error.status == 401) {
                (_a = this._logger) === null || _a === void 0 ? void 0 : _a.error(`${GoFeatureFlagWebProvider.name}: invalid token used to contact GO Feature Flag instance: ${error}`);
            }
            else if (error.status === 404) {
                (_b = this._logger) === null || _b === void 0 ? void 0 : _b.error(`${GoFeatureFlagWebProvider.name}: impossible to call go-feature-flag relay proxy ${error}`);
            }
            else {
                (_c = this._logger) === null || _c === void 0 ? void 0 : _c.error(`${GoFeatureFlagWebProvider.name}: unknown error while retrieving flags: ${error}`);
            }
        }
        else {
            (_d = this._logger) === null || _d === void 0 ? void 0 : _d.error(`${GoFeatureFlagWebProvider.name}: unknown error while retrieving flags: ${error}`);
            this.events.emit(ProviderEvents.Error, {
                message: 'unknown error while retrieving flags',
            });
        }
    }
}

export { GoFeatureFlagWebProvider };
